import { WaypointType } from '@caresend/types';
import dayjs from 'dayjs';
import { defineComponent, computed, ref, watch, onMounted, onUnmounted } from 'vue';
import Warning from '../../../../../../assets/icons/circle-danger.svg.js';
import Warning$1 from '../../../../../../assets/icons/warning-2.svg.js';
import __vue_component__ from '../../../../../cards/BasicCard/BasicCard.vue.js';
import { CardStyle } from '../../../../../cards/BasicCard/model.js';
import { usePortalStore } from '../../../store/portal/index.js';
import { ConfirmDepartureModalMetaVariants } from '../../../store/portal/workflow/items/waypoint.js';
import { getItineraryDrivingEstimateRequest } from '../../../../../../database/firebaseConfig/cloudFunctions.js';

// Help us identify which warning to show and prevent the user from
// Triggering notifications to the patient or agency
var DepartureVariant;
(function (DepartureVariant) {
    DepartureVariant["WITHIN_BIG_WINDOW"] = "withinBigWindow";
    DepartureVariant["WITHIN_SMALL_WINDOW"] = "withinSmallWindow";
    DepartureVariant["OUTSIDE_BIG_WINDOW"] = "outsideBigWindow";
})(DepartureVariant || (DepartureVariant = {}));
const TWO_HOURS = 2;
const DepartureToModalMetaVariant = {
    [DepartureVariant.WITHIN_SMALL_WINDOW]: undefined,
    [DepartureVariant.WITHIN_BIG_WINDOW]: ConfirmDepartureModalMetaVariants.WITHIN_BIG_WINDOW,
    [DepartureVariant.OUTSIDE_BIG_WINDOW]: ConfirmDepartureModalMetaVariants.OUTSIDE_BIG_WINDOW,
};
var script = defineComponent({
    name: 'ItineraryWaypointConfirmDeparture',
    components: {
        BasicCard: __vue_component__,
        CircleDanger: Warning,
        Warning: Warning$1,
    },
    // this deals with the parent pane's button status
    model: {
        prop: 'value',
        event: 'update:value',
    },
    props: {
        waypointID: {
            type: String,
            required: true,
        },
        value: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:value', 'setModalMetaVariant', 'displayFooter'],
    setup(props, { emit }) {
        const store = usePortalStore();
        const waypoint = computed(() => store.waypoints.get(props.waypointID));
        const shift = computed(() => store.itinerary.value.shiftID
            ? store.shifts.getRaw(store.itinerary.value.shiftID)
            : null);
        const transitDuration = ref(undefined);
        const estimatedArrivalTime = computed(() => {
            if (!transitDuration.value)
                return;
            return transitDuration.value + Date.now();
        });
        const estimatedArrivalTimeText = computed(() => {
            if (!estimatedArrivalTime.value)
                return;
            const arriveToday = dayjs(estimatedArrivalTime.value).isSame(dayjs(), 'day');
            const formatString = arriveToday ? 'h:mm a' : 'ddd, MMM D h:mm a';
            const estimatedTime = dayjs(estimatedArrivalTime.value).format(formatString);
            return `Estimated arrival is ${estimatedTime}`;
        });
        const isLoading = computed(() => transitDuration.value === undefined);
        /**
         * Enhancement idea
         *
         * if the visit is scheduled for a different day, tell the user this
         */
        const scheduledTimeText = computed(() => store.waypoints.getters.scheduledTimeText(props.waypointID));
        const scheduledTimeDateOnly = computed(() => store.waypoints.getters.scheduledDateTime(props.waypointID));
        /**
         * Whether the current driving estimate is within a specific time window:
         * - WITHIN_SMALL_WINDOW: within 2 hours of expected arrival time
         * - WITHIN_BIG_WINDOW: within same day of expected arrival time
         * - OUTSIDE_BIG_WINDOW: on a different day of expected arrival time
         */
        const departureVariant = computed(() => {
            var _a, _b;
            const scheduledTime = (_b = (_a = waypoint.value) === null || _a === void 0 ? void 0 : _a.date) === null || _b === void 0 ? void 0 : _b.timestamp;
            if (!estimatedArrivalTime.value || !scheduledTime)
                return DepartureVariant.WITHIN_SMALL_WINDOW;
            const arrivalTimeDate = dayjs(estimatedArrivalTime.value);
            const scheduledTimeDate = dayjs(scheduledTime);
            const timeWindow = scheduledTimeDate.diff(arrivalTimeDate, 'hours');
            const isSameDay = scheduledTimeDate.isSame(arrivalTimeDate, 'day');
            if (timeWindow < TWO_HOURS)
                return DepartureVariant.WITHIN_SMALL_WINDOW;
            if (isSameDay)
                return DepartureVariant.WITHIN_BIG_WINDOW;
            return DepartureVariant.OUTSIDE_BIG_WINDOW;
        });
        const cardStyle = computed(() => departureVariant.value === DepartureVariant.WITHIN_SMALL_WINDOW
            ? CardStyle.WHITE_WITH_BORDER_WARNING
            : CardStyle.WHITE_WITH_BORDER_DANGER);
        const recipientType = computed(() => {
            var _a;
            return ((_a = store.waypoints.getRaw(props.waypointID)) === null || _a === void 0 ? void 0 : _a.type) === WaypointType.PATIENT
                ? 'patient'
                : 'agency';
        });
        const notificationText = computed(() => `This action will notify the ${recipientType.value} that you are on your way`);
        const titleText = computed(() => {
            if (isLoading.value)
                return 'Loading driving information...';
            return departureVariant.value !== DepartureVariant.WITHIN_SMALL_WINDOW
                ? 'It’s too early to start driving'
                : notificationText.value;
        });
        // If the departure value is OUTSIDE_BIG_WINDOW, disable the next-step button
        watch(departureVariant, (newVal, oldVal) => {
            emit('setModalMetaVariant', DepartureToModalMetaVariant[newVal]);
            // only update the value when it changes to OUTSIDE_BIG_WINDOW or not
            if (newVal !== DepartureVariant.OUTSIDE_BIG_WINDOW
                && oldVal !== DepartureVariant.OUTSIDE_BIG_WINDOW
                && oldVal !== undefined)
                return;
            emit('update:value', newVal === DepartureVariant.OUTSIDE_BIG_WINDOW);
        }, { immediate: true });
        // If the estimated arrival time is set, display the footer
        watch(estimatedArrivalTime, (newVal, oldVal) => {
            // check if undefined since 0 is seen as falsey. Avoid unecessary emits
            const isNewValSet = newVal !== undefined;
            const isOldValSet = oldVal !== undefined;
            if (isNewValSet === isOldValSet)
                return;
            emit('displayFooter', isNewValSet);
        }, { immediate: true });
        // get the driving estimate
        onMounted(async () => {
            var _a, _b, _c, _d;
            const leaveFrom = (_c = (_b = (_a = store.itinerary.getters.assignedNurse()) === null || _a === void 0 ? void 0 : _a.location) === null || _b === void 0 ? void 0 : _b.current) !== null && _c !== void 0 ? _c : (_d = shift.value) === null || _d === void 0 ? void 0 : _d.leaveFrom;
            if (!leaveFrom)
                return;
            transitDuration.value = await getItineraryDrivingEstimateRequest({
                leaveFrom,
                itineraryID: store.itinerary.value.id,
                waypointID: props.waypointID,
            });
        });
        // A bit jank, but to avoid the footer from starting to show when the modal is re-opened
        onUnmounted(() => { emit('displayFooter', undefined); });
        return {
            CardStyle,
            cardStyle,
            departureVariant,
            estimatedArrivalTimeText,
            isLoading,
            notificationText,
            recipientType,
            scheduledTimeText,
            scheduledTimeDateOnly,
            titleText,
        };
    },
});

export { script as default };
